import * as XLSX from 'xlsx';

import type { PositionDraft } from '../../../types/project';
import {
    DEFAULT_UNIT,
    POSITIONS_TEMPLATE_HEADERS,
    TEMPLATE_LEGEND_VALUES,
    TEMPLATE_ROW_NUMBER_HEADERS,
} from '../model/constants';

type DraftKey =
    | 'name'
    | 'spec'
    | 'qty'
    | 'unit'
    | 'region'
    | 'priceFrom'
    | 'priceTo'
    | 'requestPrice'
    | 'analogs'
    | 'logistics';

const normalize = (raw: string): string =>
    raw
        .toLowerCase()
        .replace(/\([^)]*\)/g, ' ')
        .replace(/ё/g, 'е')
        .replace(/[*:.,;"']/g, ' ')
        .replace(/[\s\u00a0]+/g, ' ')
        .trim();

const HEADER_LOOKUP: Record<string, DraftKey> = Object.entries(
    POSITIONS_TEMPLATE_HEADERS,
).reduce<Record<string, DraftKey>>((acc, [key, aliases]) => {
    aliases.forEach((alias) => {
        acc[normalize(alias)] = key as DraftKey;
    });
    return acc;
}, {});

const ROW_NUMBER_LOOKUP = new Set(TEMPLATE_ROW_NUMBER_HEADERS.map(normalize));
const LEGEND_LOOKUP = new Set(TEMPLATE_LEGEND_VALUES.map(normalize));

const TRUTHY = new Set(['да', 'true', '1', 'yes', 'y', '+', 'нужны', 'нужна', 'x', 'х']);

const toBoolean = (value: unknown): boolean => TRUTHY.has(String(value ?? '').trim().toLowerCase());

const toNumber = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const cleaned = String(value ?? '')
        .replace(/[\s\u00a0]/g, '')
        .replace(',', '.');

    if (!cleaned) return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

const toPrice = (value: unknown): number | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) && value >= 0 ? value : null;
    }

    const cleaned = String(value ?? '')
        .replace(/₽|руб\.?|р\.|rub/gi, '')
        .replace(/[\s\u00a0]/g, '')
        .replace(',', '.');

    if (!cleaned) return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const toText = (value: unknown): string => String(value ?? '').trim();

const toCleanText = (value: unknown): string =>
    toText(value).replace(/[\s\u00a0]+/g, ' ');

const toUnit = (value: unknown): string =>
    toCleanText(value).replace(/\.+$/, '') || DEFAULT_UNIT;

export interface ParsedPositionsFile {
    drafts: PositionDraft[];
    skippedRows: number;
    unknownHeaders: string[];
}

export async function parsePositionsFile(file: File): Promise<ParsedPositionsFile> {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });

    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
        throw new Error('В файле нет ни одного листа');
    }

    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], {
        defval: '',
        raw: true,
    });

    const drafts: PositionDraft[] = [];
    const unknown = new Set<string>();
    let skippedRows = 0;

    rows.forEach((row) => {
        const draft: PositionDraft = {
            name: '',
            spec: '',
            qty: 1,
            unit: DEFAULT_UNIT,
            region: '',
            priceFrom: 0,
            priceTo: 0,
            requestPrice: null,
            analogs: false,
            logistics: false,
        };

        let rowNumberCell: string | null = null;

        Object.entries(row).forEach(([header, value]) => {
            const normalizedHeader = normalize(header);

            if (ROW_NUMBER_LOOKUP.has(normalizedHeader)) {
                rowNumberCell = toText(value);
                return;
            }

            const key = HEADER_LOOKUP[normalizedHeader];

            if (!key) {
                // Пустые технические колонки Excel («__EMPTY») ошибкой не считаем
                if (toText(value)) unknown.add(header.trim());
                return;
            }

            switch (key) {
                case 'qty':
                    draft.qty = toNumber(value) ?? 1;
                    break;
                case 'priceFrom':
                    // 0 в UI означает «без ограничения» — так же, как в positionToBody
                    draft.priceFrom = toNumber(value) ?? 0;
                    break;
                case 'priceTo':
                    draft.priceTo = toNumber(value) ?? 0;
                    break;
                case 'requestPrice':
                    draft.requestPrice = toPrice(value);
                    break;
                case 'analogs':
                    draft.analogs = toBoolean(value);
                    break;
                case 'logistics':
                    draft.logistics = toBoolean(value);
                    break;
                case 'region':
                    draft.region = toCleanText(value);
                    break;
                case 'name':
                    draft.name = toCleanText(value);
                    break;
                case 'spec':
                    draft.spec = toCleanText(value);
                    break;
                default:
                    break;
            }
        });

        const isLegendRow =
            LEGEND_LOOKUP.has(normalize(draft.name)) ||
            draft.name.startsWith('*') ||
            (rowNumberCell !== null && rowNumberCell !== '' && toNumber(rowNumberCell) === null);

        if (isLegendRow) return;

        if (!draft.name) {
            const hasAnyValue = Object.values(row).some((value) => toText(value));
            if (hasAnyValue) skippedRows += 1;
            return;
        }

        drafts.push(draft);
    });

    return { drafts, skippedRows, unknownHeaders: Array.from(unknown) };
}